import { useFormContext } from '@/shared/hooks/useForm'
import { Controller } from 'react-hook-form'
import { z } from 'zod'
import Select from './Select'
import { Currency } from '@/shared/models/moneyModel'
import { Path, PathValue } from 'react-hook-form'

type ZodInfer<T extends z.ZodType> = z.infer<T>
type FieldValue<TSchema extends z.ZodObject<any>> = PathValue<ZodInfer<TSchema>, Path<ZodInfer<TSchema>>>

type SelectOption<TValue> = {
    value: TValue
    label: string
}

type ControlledCurrencySelectProps<TSchema extends z.ZodObject<any>> = {
    currencies: Currency[]
    name: Path<ZodInfer<TSchema>>
    label?: string
    placeholder?: string
    schema: TSchema
    onChange?: (option: SelectOption<TSchema> | null) => void
}

const ControlledCurrencySelect = <TSchema extends z.ZodObject<any>>({
    currencies,
    name,
    label,
    placeholder,
    schema,
    onChange,
    ...props
}: ControlledCurrencySelectProps<TSchema>) => {
    const { register, control } = useFormContext({ schema })

    const options: SelectOption<FieldValue<TSchema>>[] = currencies.map((currency) => ({
        value: currency as FieldValue<TSchema>,
        label: currency
    }))

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: register(name).required }}
            render={({ field: { value, onChange: internalOnChange } }) => (
                <Select
                    {...register(name)}
                    isClearable
                    label={label}
                    placeholder={placeholder}
                    value={
                        value
                            ? {
                                  value: value,
                                  label: value as string
                              }
                            : null
                    }
                    onChange={(option) => {
                        onChange?.(option)
                        internalOnChange(option?.value || null)
                    }}
                    options={options}
                    {...props}
                />
            )}
        />
    )
}

export default ControlledCurrencySelect
